import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Save, RotateCcw, ImagePlus, History } from "lucide-react";
import { HERO_BG, ABOUT_PORTRAIT, SAGE_TEXTURE, PILLAR_IMAGES } from "@/lib/images";

// Edytor tresci landing page. Pobiera /content, edytuje strukture, zapisuje
// przez PUT /admin/content. Tresc jest zrodlem prawdy w bazie - strona glowna
// renderuje z tych samych danych.
export default function ContentEditor() {
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [revisions, setRevisions] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [restoring, setRestoring] = useState(false);

  const BRAND_DEFAULT = {
    part1: "Bezpieczna", part2: "Przestrzeń",
    weight1: "700", weight2: "700",
    color1: "#000000", color2: "#4C5A49",
    size1: "36", size2: "36",
  };
  const load = () =>
    api.get("/content")
      .then(({ data }) => setContent({ ...data, brand: { ...BRAND_DEFAULT, ...(data?.brand || {}) } }))
      .catch(() => toast.error("Nie udało się pobrać treści"));
  const loadRevisions = () =>
    api.get("/admin/content/revisions").then(({ data }) => setRevisions(data)).catch(() => {});

  useEffect(() => { load(); loadRevisions(); }, []);

  const restore = async (id) => {
    if (!window.confirm("Przywrócić tę wersję? Obecna treść trafi do historii (też będzie można ją cofnąć).")) return;
    setRestoring(true);
    try {
      const res = await api.post(`/admin/content/restore/${id}`);
      setContent(res.data);
      loadRevisions();
      toast.success("Wersja przywrócona. Odśwież stronę główną, by zobaczyć efekt.");
    } catch (e) {
      toast.error("Nie udało się przywrócić wersji");
    } finally {
      setRestoring(false);
    }
  };

  if (!content) return <p className="text-[#5C6670] py-12 text-center" data-testid="content-loading">Ładowanie treści…</p>;

  const setField = (section, key, value) =>
    setContent((c) => ({ ...c, [section]: { ...c[section], [key]: value } }));
  const setList = (section, key, value) =>
    setContent((c) => ({ ...c, [section]: { ...c[section], [key]: value } }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/content", content);
      setContent(data);
      loadRevisions();
      toast.success("Treść zapisana. Odśwież stronę główną, by zobaczyć zmiany.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Błąd zapisu");
    } finally {
      setSaving(false);
    }
  };

  const br = content.brand || {};
  const h = content.hero || {};
  const a = content.about || {};
  const o = content.offer || {};
  const ph = content.philosophy || {};
  const t = content.testimonials || {};
  const b = content.booking || {};
  const f = content.faq || {};
  const ft = content.footer || {};

  return (
    <div className="space-y-8" data-testid="content-editor">
      <div className="flex items-center justify-between sticky top-16 bg-[#F9F8F6] py-3 z-20 -mt-3">
        <p className="text-[#5C6670] text-sm">Edytuj teksty strony głównej. Zmiany są widoczne po zapisaniu i odświeżeniu strony.</p>
        <div className="flex gap-3">
          <button onClick={() => setShowHistory((s) => !s)} className="text-[#5C6670] hover:text-[#2B2B2B] text-sm inline-flex items-center gap-2 px-4 py-2" data-testid="content-history-toggle">
            <History size={14} /> Historia zmian
          </button>
          <button onClick={load} className="text-[#5C6670] hover:text-[#2B2B2B] text-sm inline-flex items-center gap-2 px-4 py-2" data-testid="content-reload">
            <RotateCcw size={14} /> Przywróć zapisane
          </button>
          <button onClick={save} disabled={saving} data-testid="content-save"
            className="bg-[#2B2B2B] text-white rounded-full px-6 py-2.5 text-sm hover:bg-[#8A9A86] disabled:opacity-50 inline-flex items-center gap-2">
            <Save size={15} /> {saving ? "Zapisywanie…" : "Zapisz zmiany"}
          </button>
        </div>
      </div>

      {showHistory && (
        <div className="bg-white border border-[#E5E0D8] rounded-2xl p-6" data-testid="content-history">
          <h3 className="font-display text-xl text-[#2B2B2B] mb-1">Historia zmian</h3>
          <p className="text-[#5C6670] text-sm mb-4">Każdy zapis tworzy wersję. Możesz wrócić do dowolnej z ostatnich {revisions.length || 20}. Przywrócenie również zapisuje obecną treść, więc też je cofniesz.</p>
          {revisions.length === 0 ? (
            <p className="text-[#5C6670] text-sm">Brak wcześniejszych wersji. Pierwsza pojawi się tu po następnym zapisie.</p>
          ) : (
            <ul className="divide-y divide-[#E5E0D8]">
              {revisions.map((r) => (
                <li key={r.id} className="flex items-center justify-between gap-4 py-3">
                  <div className="min-w-0">
                    <p className="text-sm text-[#2B2B2B]">{formatDate(r.created_at)}</p>
                    {r.preview ? <p className="text-xs text-[#5C6670] truncate">{r.preview}</p> : null}
                  </div>
                  <button onClick={() => restore(r.id)} disabled={restoring} data-testid={`restore-${r.id}`}
                    className="shrink-0 text-[#8A9A86] hover:text-[#2B2B2B] text-sm inline-flex items-center gap-1.5 border border-[#E5E0D8] rounded-full px-4 py-1.5 hover:border-[#8A9A86] disabled:opacity-50">
                    <RotateCcw size={13} /> Przywróć
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* MARKA / LOGO */}
      <Section title="Marka — napis w górnym menu">
        <div className="grid sm:grid-cols-2 gap-4">
          <Text label="Nazwa — część 1" value={br.part1} onChange={(v) => setField("brand", "part1", v)} />
          <Text label="Nazwa — część 2 (wyróżniona kolorem)" value={br.part2} onChange={(v) => setField("brand", "part2", v)} />
        </div>
        <p className="text-xs text-[#5C6670] -mt-1">
          Czcionka logo (Cormorant Garamond) ma realnie dostępne grubości 300–700 — powyżej 700 przeglądarka i tak pokaże Bold, więc nie ma sensu tego wybierać.
        </p>
        <div className="grid sm:grid-cols-3 gap-4">
          <WeightField label="Grubość — część 1" value={br.weight1} onChange={(v) => setField("brand", "weight1", v)} />
          <ColorField label="Kolor — część 1" value={br.color1} onChange={(v) => setField("brand", "color1", v)} />
          <SizeField label="Rozmiar — część 1 (px)" value={br.size1} onChange={(v) => setField("brand", "size1", v)} />
          <WeightField label="Grubość — część 2" value={br.weight2} onChange={(v) => setField("brand", "weight2", v)} />
          <ColorField label="Kolor — część 2" value={br.color2} onChange={(v) => setField("brand", "color2", v)} />
          <SizeField label="Rozmiar — część 2 (px)" value={br.size2} onChange={(v) => setField("brand", "size2", v)} />
        </div>
      </Section>

      {/* HERO */}
      <Section title="Nagłówek (Hero)">
        <Text label="Nadtytuł" value={h.overline} onChange={(v) => setField("hero", "overline", v)} />
        <div className="grid sm:grid-cols-3 gap-4">
          <Text label="Nagłówek — przed" value={h.heading_before} onChange={(v) => setField("hero", "heading_before", v)} />
          <Text label="Nagłówek — wyróżnione" value={h.heading_em} onChange={(v) => setField("hero", "heading_em", v)} />
          <Text label="Nagłówek — po" value={h.heading_after} onChange={(v) => setField("hero", "heading_after", v)} />
        </div>
        <Area label="Podtytuł" value={h.subheading} onChange={(v) => setField("hero", "subheading", v)} />
        <div className="grid sm:grid-cols-2 gap-4">
          <Text label="Przycisk główny" value={h.cta_primary} onChange={(v) => setField("hero", "cta_primary", v)} />
          <Text label="Przycisk drugi" value={h.cta_secondary} onChange={(v) => setField("hero", "cta_secondary", v)} />
        </div>
        <ImageField label="Zdjęcie w tle (hero)" value={h.background_image} fallback={HERO_BG} onChange={(v) => setField("hero", "background_image", v)} />
        <ObjectList
          label="Liczby (boczne)" items={h.stats || []} onChange={(v) => setList("hero", "stats", v)}
          fields={[{ key: "value", label: "Wartość" }, { key: "label", label: "Opis" }]}
          makeEmpty={() => ({ value: "", label: "" })}
        />
      </Section>

      {/* O MNIE */}
      <Section title="O mnie">
        <Text label="Nadtytuł" value={a.overline} onChange={(v) => setField("about", "overline", v)} />
        <Text label="Nagłówek" value={a.heading} onChange={(v) => setField("about", "heading", v)} />
        <ImageField label="Zdjęcie (portret)" value={a.portrait_image} fallback={ABOUT_PORTRAIT} onChange={(v) => setField("about", "portrait_image", v)} />
        <Area label="Cytat na zdjęciu" value={a.card_quote} onChange={(v) => setField("about", "card_quote", v)} />
        <StringList label="Akapity" items={a.paragraphs || []} onChange={(v) => setList("about", "paragraphs", v)} area />
        <ObjectList
          label="Liczby" items={a.stats || []} onChange={(v) => setList("about", "stats", v)}
          fields={[{ key: "n", label: "Liczba" }, { key: "l", label: "Opis" }]}
          makeEmpty={() => ({ n: "", l: "" })}
        />
      </Section>

      {/* OFERTA */}
      <Section title="Oferta — filary">
        <Text label="Nadtytuł" value={o.overline} onChange={(v) => setField("offer", "overline", v)} />
        <Text label="Nagłówek" value={o.heading} onChange={(v) => setField("offer", "heading", v)} />
        <Area label="Wstęp" value={o.intro} onChange={(v) => setField("offer", "intro", v)} />
        <ObjectList
          label="Filary" items={o.pillars || []} onChange={(v) => setList("offer", "pillars", v)}
          fields={[
            { key: "overline", label: "Nadtytuł (np. I · Korzeń)" },
            { key: "title", label: "Tytuł filaru" },
            { key: "desc", label: "Opis", type: "area" },
            { key: "image", label: "Zdjęcie filaru", type: "image", fallback: (i) => PILLAR_IMAGES[i % PILLAR_IMAGES.length] },
            { key: "services", label: "Usługi", type: "stringlist" },
          ]}
          makeEmpty={() => ({ overline: "", title: "", desc: "", image: "", services: [] })}
        />
      </Section>

      {/* CYTAT */}
      <Section title="Pasek z cytatem">
        <Text label="Nadtytuł" value={ph.overline} onChange={(v) => setField("philosophy", "overline", v)} />
        <Area label="Cytat" value={ph.quote} onChange={(v) => setField("philosophy", "quote", v)} rows={4} />
        <ImageField label="Zdjęcie w tle" value={ph.background_image} fallback={SAGE_TEXTURE} onChange={(v) => setField("philosophy", "background_image", v)} />
      </Section>

      {/* OPINIE */}
      <Section title="Opinie">
        <Text label="Nadtytuł" value={t.overline} onChange={(v) => setField("testimonials", "overline", v)} />
        <Text label="Nagłówek" value={t.heading} onChange={(v) => setField("testimonials", "heading", v)} />
        <ObjectList
          label="Opinie" items={t.items || []} onChange={(v) => setList("testimonials", "items", v)}
          fields={[
            { key: "quote", label: "Treść", type: "area" },
            { key: "author", label: "Autor (np. M., 41 lat)" },
            { key: "context", label: "Kontekst (metoda)" },
          ]}
          makeEmpty={() => ({ quote: "", author: "", context: "" })}
        />
      </Section>

      {/* REZERWACJA */}
      <Section title="Rezerwacja (sekcja)">
        <Text label="Nadtytuł" value={b.overline} onChange={(v) => setField("booking", "overline", v)} />
        <Text label="Nagłówek" value={b.heading} onChange={(v) => setField("booking", "heading", v)} />
        <Area label="Wstęp" value={b.intro} onChange={(v) => setField("booking", "intro", v)} />
        <ObjectList
          label="Wiersze (typ sesji)" items={b.rows || []} onChange={(v) => setList("booking", "rows", v)}
          fields={[{ key: "k", label: "Etykieta" }, { key: "v", label: "Wartość" }]}
          makeEmpty={() => ({ k: "", v: "" })}
        />
      </Section>

      {/* FAQ */}
      <Section title="FAQ">
        <Text label="Nadtytuł" value={f.overline} onChange={(v) => setField("faq", "overline", v)} />
        <Text label="Nagłówek" value={f.heading} onChange={(v) => setField("faq", "heading", v)} />
        <Area label="Wstęp" value={f.intro} onChange={(v) => setField("faq", "intro", v)} />
        <ObjectList
          label="Pytania" items={f.items || []} onChange={(v) => setList("faq", "items", v)}
          fields={[
            { key: "q", label: "Pytanie" },
            { key: "a", label: "Odpowiedź", type: "area" },
          ]}
          makeEmpty={() => ({ q: "", a: "" })}
        />
      </Section>

      {/* STOPKA */}
      <Section title="Stopka — kontakt">
        <div className="grid sm:grid-cols-3 gap-4">
          <Text label="Email" value={ft.email} onChange={(v) => setField("footer", "email", v)} />
          <Text label="Telefon" value={ft.phone} onChange={(v) => setField("footer", "phone", v)} />
          <Text label="Sesje" value={ft.sessions} onChange={(v) => setField("footer", "sessions", v)} />
        </div>
      </Section>

      <div className="flex justify-end pb-12">
        <button onClick={save} disabled={saving} data-testid="content-save-bottom"
          className="bg-[#2B2B2B] text-white rounded-full px-6 py-2.5 text-sm hover:bg-[#8A9A86] disabled:opacity-50 inline-flex items-center gap-2">
          <Save size={15} /> {saving ? "Zapisywanie…" : "Zapisz zmiany"}
        </button>
      </div>
    </div>
  );
}

function formatDate(iso) {
  try { return new Date(iso).toLocaleString("pl-PL"); } catch { return iso; }
}

function Section({ title, children }) {
  return (
    <div className="bg-white border border-[#E5E0D8] rounded-2xl p-6 md:p-8">
      <h3 className="font-display text-2xl text-[#2B2B2B] mb-6">{title}</h3>
      <div className="space-y-5">{children}</div>
    </div>
  );
}

function Text({ label, value, onChange }) {
  return (
    <div>
      <label className="text-overline block mb-2">{label}</label>
      <input value={value || ""} onChange={(e) => onChange(e.target.value)}
        className="w-full bg-transparent border-b border-[#E5E0D8] py-2.5 focus:outline-none focus:border-[#8A9A86] text-[#2B2B2B]" />
    </div>
  );
}

function WeightField({ label, value, onChange }) {
  return (
    <div>
      <label className="text-overline block mb-2">{label}</label>
      <select
        value={value || "700"} onChange={(e) => onChange(e.target.value)}
        className="w-full px-4 py-2.5 rounded-xl border border-[#E5E0D8] bg-white text-sm text-[#2B2B2B]"
      >
        <option value="300">Cienka</option>
        <option value="400">Normalna</option>
        <option value="500">Średnia</option>
        <option value="600">Półgruba</option>
        <option value="700">Pogrubiona (max)</option>
      </select>
    </div>
  );
}

function SizeField({ label, value, onChange }) {
  return (
    <div>
      <label className="text-overline block mb-2">{label}</label>
      <input
        type="number" min="14" max="72" step="1"
        value={value || "36"} onChange={(e) => onChange(e.target.value)}
        className="w-full px-4 py-2.5 rounded-xl border border-[#E5E0D8] bg-white text-sm text-[#2B2B2B]"
      />
    </div>
  );
}

function ColorField({ label, value, onChange }) {
  const v = value || "#000000";
  return (
    <div>
      <label className="text-overline block mb-2">{label}</label>
      <div className="flex items-center gap-2">
        <input
          type="color" value={v} onChange={(e) => onChange(e.target.value)}
          className="h-10 w-12 rounded-lg border border-[#E5E0D8] bg-white cursor-pointer shrink-0"
        />
        <input
          value={v} onChange={(e) => onChange(e.target.value)}
          className="w-full bg-transparent border-b border-[#E5E0D8] py-2.5 focus:outline-none focus:border-[#8A9A86] text-[#2B2B2B] text-sm font-mono"
        />
      </div>
    </div>
  );
}

function Area({ label, value, onChange, rows = 3 }) {
  return (
    <div>
      <label className="text-overline block mb-2">{label}</label>
      <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={rows}
        className="w-full bg-transparent border border-[#E5E0D8] rounded-xl p-3 focus:outline-none focus:border-[#8A9A86] resize-none text-[#2B2B2B]" />
    </div>
  );
}

// Pole zdjecia: podglad + upload pliku + alternatywnie URL + przywrocenie domyslnego
function ImageField({ label, value, fallback, onChange }) {
  const [uploading, setUploading] = useState(false);
  const shown = value || fallback;

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Zdjęcie wgrane");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Błąd wgrywania zdjęcia");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div>
      <label className="text-overline block mb-2">{label}</label>
      <div className="flex gap-4 items-start">
        <div className="w-28 h-20 rounded-lg overflow-hidden bg-[#E5E0D8] shrink-0 border border-[#E5E0D8]">
          {shown ? <img src={shown} alt="" className="w-full h-full object-cover" /> : null}
        </div>
        <div className="flex-1 space-y-2">
          <div className="flex gap-3 flex-wrap items-center">
            <label className="cursor-pointer bg-[#2B2B2B] text-white rounded-full px-4 py-2 text-xs hover:bg-[#8A9A86] inline-flex items-center gap-1.5">
              <ImagePlus size={13} /> {uploading ? "Wgrywanie…" : "Wgraj zdjęcie"}
              <input type="file" accept="image/*" onChange={upload} disabled={uploading} className="hidden" />
            </label>
            {value ? (
              <button type="button" onClick={() => onChange("")} className="text-[#C88E75] hover:text-[#2B2B2B] text-xs inline-flex items-center gap-1">
                <Trash2 size={13} /> Przywróć domyślne
              </button>
            ) : null}
          </div>
          <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="lub wklej adres URL zdjęcia"
            className="w-full bg-transparent border-b border-[#E5E0D8] py-1.5 text-sm focus:outline-none focus:border-[#8A9A86] text-[#2B2B2B]" />
        </div>
      </div>
    </div>
  );
}

// Lista stringow (np. akapity, usługi)
function StringList({ label, items, onChange, area }) {
  const update = (i, val) => { const n = [...items]; n[i] = val; onChange(n); };
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const add = () => onChange([...items, ""]);
  return (
    <div>
      <label className="text-overline block mb-2">{label}</label>
      <div className="space-y-3">
        {items.map((it, i) => (
          <div key={i} className="flex gap-2 items-start">
            {area ? (
              <textarea value={it} onChange={(e) => update(i, e.target.value)} rows={2}
                className="flex-1 bg-transparent border border-[#E5E0D8] rounded-xl p-3 focus:outline-none focus:border-[#8A9A86] resize-none text-[#2B2B2B]" />
            ) : (
              <input value={it} onChange={(e) => update(i, e.target.value)}
                className="flex-1 bg-transparent border-b border-[#E5E0D8] py-2 focus:outline-none focus:border-[#8A9A86] text-[#2B2B2B]" />
            )}
            <button type="button" onClick={() => remove(i)} className="text-[#C88E75] hover:text-[#2B2B2B] mt-2"><Trash2 size={15} /></button>
          </div>
        ))}
      </div>
      <button type="button" onClick={add} className="mt-3 text-[#8A9A86] hover:text-[#2B2B2B] text-sm inline-flex items-center gap-1">
        <Plus size={14} /> Dodaj
      </button>
    </div>
  );
}

// Lista obiektow z polami (text/area/stringlist)
function ObjectList({ label, items, onChange, fields, makeEmpty }) {
  const update = (i, key, val) => { const n = [...items]; n[i] = { ...n[i], [key]: val }; onChange(n); };
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const add = () => onChange([...items, makeEmpty()]);
  return (
    <div>
      <label className="text-overline block mb-3">{label}</label>
      <div className="space-y-4">
        {items.map((item, i) => (
          <div key={i} className="border border-[#E5E0D8] rounded-xl p-4 bg-[#F9F8F6] relative">
            <button type="button" onClick={() => remove(i)} className="absolute top-3 right-3 text-[#C88E75] hover:text-[#2B2B2B]"><Trash2 size={15} /></button>
            <div className="space-y-3 pr-8">
              {fields.map((fld) => {
                if (fld.type === "stringlist") {
                  return <StringList key={fld.key} label={fld.label} items={item[fld.key] || []} onChange={(v) => update(i, fld.key, v)} />;
                }
                if (fld.type === "area") {
                  return <Area key={fld.key} label={fld.label} value={item[fld.key]} onChange={(v) => update(i, fld.key, v)} rows={3} />;
                }
                if (fld.type === "image") {
                  return <ImageField key={fld.key} label={fld.label} value={item[fld.key]} fallback={fld.fallback ? fld.fallback(i) : ""} onChange={(v) => update(i, fld.key, v)} />;
                }
                return <Text key={fld.key} label={fld.label} value={item[fld.key]} onChange={(v) => update(i, fld.key, v)} />;
              })}
            </div>
          </div>
        ))}
      </div>
      <button type="button" onClick={add} className="mt-3 text-[#8A9A86] hover:text-[#2B2B2B] text-sm inline-flex items-center gap-1">
        <Plus size={14} /> Dodaj
      </button>
    </div>
  );
}
